import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/blacksmith-ui/button";
import { Activity, LogOut, RefreshCw } from "lucide-react";
const STATUS_URL = "https://status.blacksmith.sh";
export interface GlobalErrorSplashProps {
  onRetry?: () => void;
  onLogout?: () => void;
}

/**
 * Full-screen fatal error splash for the app-level error boundary in
 * `_app.tsx`, in the same pixel-terminal family as ErrorStateDisplay and
 * GitHubRateLimitError. Retry defaults to a full reload since the boundary
 * catches arbitrary render errors.
 */
export function GlobalErrorSplash(t0) {
  const $ = _c(15);
  const {
    onRetry,
    onLogout
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div aria-hidden={true} className="pointer-events-none absolute inset-0 bg-[image:radial-gradient(var(--pattern_color)_0.5px,_transparent_0)] bg-[size:16px_16px] opacity-60 [--pattern_color:var(--color-black)]/15 dark:[--pattern_color:var(--color-white)]/7" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="type-numerals-pixel-l text-neutral-900 dark:text-neutral-100">ERR</div>;
    t3 = <div className="mt-2 h-1 w-16 rounded-full bg-blacksmith-yellow" />;
    t4 = <h1 className="mt-8 type-page-title-m text-neutral-900 dark:text-neutral-100">Uh oh! Something went wrong</h1>;
    t5 = <p className="mt-2 type-body-s-regular text-muted-foreground">We've been notified of this issue and are looking into it. Please try again.</p>;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onRetry) {
    t6 = <Button icon={RefreshCw} onClick={() => {
      if (onRetry) {
        onRetry();
      } else {
        window.location.reload();
      }
    }}>Try again</Button>;
    $[5] = onRetry;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onLogout) {
    t7 = <Button variant="secondary" icon={LogOut} onClick={onLogout}>Log out</Button>;
    $[7] = onLogout;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="mt-6 flex items-center justify-center gap-2">{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mt-8 flex w-full items-center justify-center gap-6 border-t border-[var(--color-border)] pt-4 type-code-block text-muted-foreground"><a href={STATUS_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-4 transition-colors hover:text-neutral-900 dark:hover:text-neutral-100"><Activity className="size-3.5" />status.blacksmith.sh</a><a href="mailto:support@blacksmith.sh" className="inline-flex items-center gap-1 underline decoration-dotted underline-offset-4 transition-colors hover:text-neutral-900 dark:hover:text-neutral-100">support@blacksmith.sh</a></div>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t8) {
    t10 = <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-app-background p-6">{t1}<div className="relative flex max-w-md flex-col items-center text-center">{t2}{t3}{t4}{t5}{t8}{t9}</div></div>;
    $[13] = t8;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}